import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { rememberReturnPath } from '@/components/auth/authReturn';
import { AUTH_TEXT_LINK, INVALID_FIELD } from '@/components/auth/authStyles';
import { FieldError } from '@/components/auth/FieldError';
import { invalidFieldProps, revealInvalidField } from '@/components/auth/fieldValidation';
import { MfaChallenge } from '@/components/auth/MfaChallenge';
import { PasswordPolicyHint } from '@/components/auth/PasswordPolicyHint';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FullScreenLoader } from '@/components/ui/full-screen-loader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, handleApiError } from '@/lib/api';
import { ApiError } from '@/lib/api/client';
import { rememberLoginEmail } from '@/lib/loginEmailMemory';
import { validatePassword } from '@/lib/password-policy';
import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
export default function AcceptInvitation({ token, groupRegion }) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { isAuthenticated, isLoading: authLoading, userEmail, logout, establishSession } = useAuth();
    const [, setLocation] = useLocation();
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(true);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showForgotLink, setShowForgotLink] = useState(false);
    const [mfaChallengeToken, setMfaChallengeToken] = useState(null);
    // Branch A field errors, shown under the field as well as in the toast: on
    // iOS/iPadOS the keyboard stays up after "Go" and hides the bottom toast.
    const [errors, setErrors] = useState({});
    const groupBase = groupRegion
        ? `/invitations/group/${encodeURIComponent(groupRegion)}/${token}`
        : null;
    const previewPath = groupBase ?? `/invitations/preview/${token}`;
    const joinPath = groupBase ? `${groupBase}/join` : `/invitations/${token}/join`;
    const acceptPath = groupBase ? `${groupBase}/accept` : `/invitations/accept/${token}`;
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const data = await apiRequest('GET', previewPath);
                if (!cancelled) {
                    setPreview(data);
                    setIsLoadingPreview(false);
                }
            }
            catch (error) {
                if (cancelled)
                    return;
                const status = error instanceof ApiError ? error.status : 0;
                const message = status === 404 ? t('invitation.invalidNotFound') : t('invitation.invalidGeneric');
                setPreviewError({ status, message });
                setIsLoadingPreview(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [previewPath, t]);
    if (authLoading || isLoadingPreview) {
        return _jsx(FullScreenLoader, {});
    }
    if (previewError || !preview) {
        return (_jsx("div", { className: "bg-background flex min-h-dvh items-center justify-center p-4", children: _jsxs(Card, { className: "w-full max-w-md", children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx(CardTitle, { className: "text-2xl font-bold", children: t('invitation.invalidTitle') }), _jsx(CardDescription, { children: previewError?.message ?? t('invitation.invalidGeneric') })] }), _jsx(CardContent, { children: _jsx(Link, { href: "/login", children: _jsx(Button, { className: "w-full", children: t('resetPassword.goToLogin') }) }) })] }) }));
    }
    const targetName = preview.clinicName ?? preview.groupName ?? '';
    const heading = preview.clinicName
        ? t('invitation.joinHeadingClinic', { clinicName: preview.clinicName })
        : t('invitation.joinHeadingGroup', { groupName: preview.groupName ?? '' });
    // After the second factor: a clinic invitation was already accepted with the password; a group
    // invitation adds nobody until the second factor has passed, so accept it now as that session.
    const finishAfterMfa = async () => {
        if (groupBase) {
            try {
                await apiRequest('POST', joinPath);
            }
            catch (error) {
                handleApiError(error);
                setLocation('/');
                return;
            }
        }
        toast({
            title: t('invitation.title'),
            description: t('invitation.joinedToast', { name: targetName }),
        });
        setLocation('/');
    };
    // MFA challenge step — membership is provisioned, session withheld until second factor passes.
    if (mfaChallengeToken) {
        return (_jsx("div", { className: "bg-background flex min-h-dvh items-center justify-center p-4", children: _jsxs(Card, { className: "w-full max-w-md", children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx("div", { className: "mb-4 flex justify-center", children: _jsx("img", { src: "/newlumen.svg", alt: "New Lumen", className: "h-16 w-auto" }) }), _jsx(CardTitle, { className: "text-2xl font-bold", children: t('login.mfa.title') })] }), _jsx(CardContent, { children: _jsx(MfaChallenge, { challengeToken: mfaChallengeToken, email: preview.email, onSuccess: () => void finishAfterMfa(), onCancel: () => {
                                // The invitation was already consumed when the account was
                                // provisioned, so the password form is a dead end (re-submit 410s).
                                // Send the user to sign in normally instead — their account is ready.
                                toast({
                                    title: t('invitation.mfaCancelledTitle'),
                                    description: t('invitation.mfaCancelledBody'),
                                });
                                setLocation('/login');
                            } }) })] }) }));
    }
    // Branch C — signed in as a different account than the invite
    if (isAuthenticated && userEmail && userEmail.toLowerCase() !== preview.email.toLowerCase()) {
        const handleSwitch = async () => {
            setIsSubmitting(true);
            try {
                await logout({ skipRedirect: true });
                // The page re-renders as Branch A on the same URL once auth state flips.
            }
            finally {
                setIsSubmitting(false);
            }
        };
        return (_jsx("div", { className: "bg-background flex min-h-dvh items-center justify-center p-4", children: _jsxs(Card, { className: "w-full max-w-md", children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx(CardTitle, { className: "text-2xl font-bold", children: t('invitation.mismatchHeading') }), _jsx(CardDescription, { children: t('invitation.mismatchBody', {
                                    currentEmail: userEmail,
                                    inviteEmail: preview.email,
                                }) })] }), _jsxs(CardContent, { className: "space-y-2", children: [_jsx(Button, { className: "w-full", onClick: handleSwitch, loading: isSubmitting, disabled: isSubmitting, children: t('invitation.mismatchSwitch') }), _jsx(Link, { href: "/", children: _jsx(Button, { variant: "outline", className: "w-full", children: t('invitation.cancelButton') }) })] })] }) }));
    }
    // Branch B — signed in as the invited email
    if (isAuthenticated && userEmail && userEmail.toLowerCase() === preview.email.toLowerCase()) {
        const acceptHeading = preview.clinicName
            ? t('invitation.acceptHeadingClinic', { clinicName: preview.clinicName })
            : t('invitation.acceptHeadingGroup', { groupName: preview.groupName ?? '' });
        const handleJoin = async () => {
            setIsSubmitting(true);
            try {
                await apiRequest('POST', joinPath);
                toast({
                    title: t('invitation.title'),
                    description: t('invitation.joinedToast', { name: targetName }),
                });
                setLocation('/');
            }
            catch (error) {
                handleApiError(error);
                setIsSubmitting(false);
            }
        };
        return (_jsx("div", { className: "bg-background flex min-h-dvh items-center justify-center p-4", children: _jsxs(Card, { className: "w-full max-w-md", children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx("div", { className: "mb-4 flex justify-center", children: _jsx("img", { src: "/newlumen.svg", alt: "New Lumen", className: "h-16 w-auto" }) }), _jsx(CardTitle, { className: "text-2xl font-bold", children: acceptHeading }), _jsx(CardDescription, { children: t('invitation.acceptBody', {
                                    inviterName: preview.inviterName,
                                    email: preview.email,
                                }) })] }), _jsxs(CardContent, { className: "space-y-2", children: [_jsx(Button, { className: "w-full", onClick: handleJoin, loading: isSubmitting, loadingText: t('invitation.acceptingButton'), children: t('invitation.acceptButton') }), _jsx(Link, { href: "/", children: _jsx(Button, { variant: "outline", className: "w-full", children: t('invitation.cancelButton') }) })] })] }) }));
    }
    // Branch A — not logged in. Splits by preview.userExists:
    //   - existing user: single confirm-password field, no name fields
    //   - new user: first/last name + password + confirm password (signup-style)
    const rejectWith = (next) => {
        setErrors(next);
        revealInvalidField(document.getElementById(next.password ? 'password' : 'confirmPassword'));
        toast({
            title: t('validation.error'),
            description: next.password ?? next.confirm,
            variant: 'destructive',
        });
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowForgotLink(false);
        if (!password) {
            rejectWith({ password: t('validation.fillRequiredFields') });
            return;
        }
        // New-user form has a confirm-password field and a client-side policy hint.
        if (!preview.userExists) {
            const policyError = validatePassword(password);
            if (policyError) {
                rejectWith({ password: policyError });
                return;
            }
            if (password !== confirmPassword) {
                rejectWith({ confirm: t('validation.passwordsDoNotMatch') });
                return;
            }
        }
        setErrors({});
        setIsSubmitting(true);
        try {
            const result = await apiRequest('POST', acceptPath, {
                password,
                firstName: firstName || null,
                lastName: lastName || null,
            });
            // MFA-enrolled invitee: membership is provisioned but the session is
            // withheld until the second factor passes (mirrors the login gate).
            if ('mfaRequired' in result) {
                setMfaChallengeToken(result.challengeToken);
                setIsSubmitting(false);
                return;
            }
            establishSession({
                email: preview.email,
                accessToken: result.accessToken,
                csrfToken: result.csrfToken,
            });
            toast({
                title: t('invitation.title'),
                description: t('invitation.joinedToast', { name: targetName }),
            });
            setLocation('/');
        }
        catch (error) {
            if (error instanceof ApiError && error.status === 401) {
                setShowForgotLink(true);
                setErrors({ password: t('invitation.invalidPassword') });
                toast({
                    title: t('validation.error'),
                    description: t('invitation.invalidPassword'),
                    variant: 'destructive',
                });
            }
            else if (error instanceof ApiError && error.status === 400) {
                // New-user password failed PasswordPolicy. Backend returns the policy
                // message as plain text in the response body.
                setErrors({ password: error.message });
                toast({
                    title: t('validation.error'),
                    description: error.message,
                    variant: 'destructive',
                });
            }
            else if (error instanceof ApiError && (error.status === 404 || error.status === 410)) {
                setPreviewError({
                    status: error.status,
                    message: t('invitation.invalidGeneric'),
                });
            }
            else {
                handleApiError(error);
            }
            setIsSubmitting(false);
        }
    };
    return (_jsx("div", { className: "bg-background flex min-h-dvh items-center justify-center p-4", children: _jsxs(Card, { className: "w-full max-w-md", children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx("div", { className: "mb-4 flex justify-center", children: _jsx("img", { src: "/newlumen.svg", alt: "New Lumen", className: "h-16 w-auto" }) }), _jsx(CardTitle, { className: "text-2xl font-bold", children: heading }), _jsx(CardDescription, { children: t('invitation.joinSubtitle', {
                                inviterName: preview.inviterName,
                                email: preview.email,
                            }) })] }), _jsx(CardContent, { children: _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "email", children: t('invitation.email') }), _jsx(Input, { id: "email", type: "email", value: preview.email, readOnly: true, autoComplete: "username", className: "text-muted-foreground cursor-default" })] }), !preview.userExists && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "firstName", children: t('invitation.firstName') }), _jsx(Input, { id: "firstName", type: "text", placeholder: t('invitation.firstNamePlaceholder'), value: firstName, onChange: (e) => setFirstName(e.target.value), disabled: isSubmitting, autoComplete: "given-name" })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "lastName", children: t('invitation.lastName') }), _jsx(Input, { id: "lastName", type: "text", placeholder: t('invitation.lastNamePlaceholder'), value: lastName, onChange: (e) => setLastName(e.target.value), disabled: isSubmitting, autoComplete: "family-name" })] })] })), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "password", children: preview.userExists ? t('invitation.confirmPassword') : t('invitation.password') }), _jsx(PasswordInput, { id: "password", placeholder: preview.userExists
                                            ? t('invitation.confirmPlaceholder')
                                            : t('invitation.passwordPlaceholder'), value: password, onChange: (e) => {
                                            setPassword(e.target.value);
                                            setErrors((prev) => ({ ...prev, password: undefined }));
                                        }, disabled: isSubmitting, autoComplete: preview.userExists ? 'current-password' : 'new-password', enterKeyHint: preview.userExists ? 'go' : 'next', className: INVALID_FIELD, ...invalidFieldProps('password-error', errors.password, !preview.userExists && password && 'password-rules') }), _jsx(FieldError, { id: "password-error", message: errors.password }), !preview.userExists && (_jsx(PasswordPolicyHint, { id: "password-rules", password: password }))] }), !preview.userExists && (_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "confirmPassword", children: t('invitation.confirmPassword') }), _jsx(PasswordInput, { id: "confirmPassword", placeholder: t('invitation.confirmPlaceholder'), value: confirmPassword, onChange: (e) => {
                                            setConfirmPassword(e.target.value);
                                            setErrors((prev) => ({ ...prev, confirm: undefined }));
                                        }, disabled: isSubmitting, autoComplete: "new-password", enterKeyHint: "go", className: INVALID_FIELD, ...invalidFieldProps('confirm-password-error', errors.confirm) }), _jsx(FieldError, { id: "confirm-password-error", message: errors.confirm })] })), showForgotLink && (_jsx("div", { className: "text-center text-sm", children: _jsx(Link, { href: "/forgot-password", className: cn(AUTH_TEXT_LINK, 'text-primary'), onClick: () => {
                                        rememberLoginEmail(preview.email);
                                        rememberReturnPath('signIn', '/login');
                                    }, children: t('invitation.forgotPassword') }) })), _jsx(Button, { type: "submit", className: "w-full", loading: isSubmitting, loadingText: t('invitation.continuingButton'), children: preview.userExists ? t('invitation.continueButton') : t('invitation.createAccount') })] }) })] }) }));
}
